import { useRef, useState } from 'react';
import { Camera, Loader2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { BACKEND_URL } from '../lib/backend';
import { toast } from 'sonner';

/**
 * Circular photo preview + device upload button + "paste a link" URL fallback.
 * `uploadFn(formData)` must resolve to the uploaded image URL string.
 */
export function PhotoUploadField({ value, onChange, uploadFn, label = 'Photo', testIdPrefix = 'photo' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewSrc = value
    ? (value.startsWith('/') ? `${BACKEND_URL}${value}` : value)
    : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = await uploadFn(fd);
      onChange(url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload that image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#1a1a1a] border-2 border-[#262626] overflow-hidden flex items-center justify-center shrink-0" data-testid={`${testIdPrefix}-preview`}>
          {previewSrc ? (
            <img src={previewSrc} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-[#555]" />
          )}
        </div>
        <div className="space-y-1">
          <Button
            type="button" variant="outline" size="sm"
            className="border-[#404040] hover:border-lime"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {uploading ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading…</>) : (<><Camera className="w-4 h-4 mr-2" />Upload a photo</>)}
          </Button>
          <p className="text-xs text-[#666]">JPEG, PNG, WebP or GIF — up to 5MB</p>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid={`${testIdPrefix}-input`} />
      </div>
      <div className="pt-1">
        <Label className="text-xs text-[#A1A1AA]">…or paste a link to the photo</Label>
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="input-dark mt-1.5" placeholder="https://..." data-testid={`${testIdPrefix}-url-input`} />
      </div>
    </div>
  );
}
